import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Select, Row, Col, Typography, Card, Tag } from 'antd';
import { 
    ShopOutlined, InboxOutlined, PlusOutlined, SearchOutlined, 
    EnvironmentOutlined, PhoneOutlined, MailOutlined, EditOutlined,
    UserOutlined, DatabaseOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';

const { Option } = Select;
const { Title, Text } = Typography;

const Shops = () => {
    const [shops, setShops] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingShop, setEditingShop] = useState(null);
    const [isUpdate, setIsUpdate] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchShops();
    }, [search]);

    const fetchShops = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/shops', {
                params: { search }
            });
            if (response.data.shops) {
                setShops(response.data.shops);
            } else {
                message.error('Failed to fetch shops.');
                setShops([]);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch shops.');
            setShops([]);
        } finally {
            setLoading(false);
        }
    };

    const showModal = (shop = null) => {
        if (shop) {
            setEditingShop(shop);
            setIsUpdate(true);
            form.setFieldsValue(shop);
        } else {
            setEditingShop(null);
            setIsUpdate(false);
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleOk = async (values) => {
        try {
            if (editingShop) {
                await axiosInstance.put(`/shops/update/${editingShop.id}`, values);
                message.success('Shop updated successfully');
            } else {
                await axiosInstance.post('/shops/register', values);
                message.success('Shop added successfully');
            }
            fetchShops();
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            console.error('Error saving shop:', error);
            if (error.response) {
                const msg = error.response.data?.message || 'Failed to save shop.';
                message.error(msg);
            } else {
                message.error('Failed to save shop.');
            }
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const shopCount = shops.filter(s => s.type === 'shop').length;
    const warehouseCount = shops.filter(s => s.type === 'warehouse').length;

    const columns = [
        {
            title: 'Branch / Outlet',
            key: 'outlet',
            render: (_, record) => {
                const isShop = record.type === 'shop';
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            backgroundColor: isShop ? '#e6f7ff' : '#feffe6',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: '12px'
                        }}>
                            {isShop ? <ShopOutlined style={{ color: '#1890ff', fontSize: '18px' }} /> : <DatabaseOutlined style={{ color: '#d4b106', fontSize: '18px' }} />}
                        </div>
                        <div>
                            <Text strong style={{ fontSize: '14px', color: '#1a1a1a' }}>{record.name}</Text>
                            <div>
                                <Tag color={isShop ? 'blue' : 'warning'} style={{ marginTop: '4px', textTransform: 'capitalize', borderRadius: '4px' }}>
                                    {record.type}
                                </Tag>
                            </div>
                        </div>
                    </div>
                );
            },
            width: 220
        },
        {
            title: 'Manager / Owner',
            dataIndex: 'owner_name',
            key: 'owner_name',
            render: (text) => (
                <span>
                    <UserOutlined style={{ marginRight: 6, color: '#bfbfbf' }} />
                    <Text>{text || 'Not assigned'}</Text>
                </span>
            ),
            width: 180
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            render: (text) => (
                <span>
                    <EnvironmentOutlined style={{ marginRight: 6, color: '#ff4d4f' }} />
                    <Text>{text}</Text>
                </span>
            ),
            width: 160
        },
        {
            title: 'Contact Details',
            key: 'contact',
            render: (_, record) => (
                <div>
                    <div><PhoneOutlined style={{ marginRight: 6, color: '#bfbfbf' }} />{record.phone}</div>
                    <div style={{ marginTop: 4 }}><MailOutlined style={{ marginRight: 6, color: '#bfbfbf' }} />{record.email}</div>
                </div>
            ),
            width: 220
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => <span style={{ color: '#8c8c8c' }}>{text || '-'}</span>,
            width: 200
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            render: (_, record) => (
                <Button 
                    type="text" 
                    icon={<EditOutlined style={{ color: '#1890ff' }} />} 
                    onClick={() => showModal(record)}
                    style={{ borderRadius: '6px' }}
                >
                    Edit
                </Button>
            ),
            width: 100
        },
    ];

    return (
        <div style={{ padding: '4px' }}>
            {/* Header Banner */}
            <div className="welcome-banner-card" style={{ marginBottom: '24px' }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                        <Title level={2} className="welcome-title" style={{ margin: 0, fontWeight: '800', letterSpacing: '-0.5px' }}>
                            Branch & Outlets Registry
                        </Title>
                        <Text className="welcome-subtitle" style={{ fontSize: '15px', display: 'block', marginTop: '6px' }}>
                            Manage your retail outlets, warehouse depots, locations, and outlet managers.
                        </Text>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                        <Button 
                            type="primary" 
                            size="large" 
                            icon={<PlusOutlined />} 
                            onClick={() => showModal()}
                            style={{ borderRadius: '8px', boxShadow: '0 4px 10px rgba(24, 144, 255, 0.25)' }}
                        >
                            Add Shop / Warehouse
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Quick Stats Grid */}
            <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12}>
                    <Card className="stat-card border-top-products" bordered={false} bodyStyle={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <span className="stat-card-title">Retail Shops</span>
                                <Title level={3} className="stat-card-value" style={{ margin: '4px 0 0 0' }}>
                                    {shopCount}
                                </Title>
                            </div>
                            <div className="stat-card-icon-container bg-products">
                                <ShopOutlined className="stat-card-icon color-products" />
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12}>
                    <Card className="stat-card border-top-invoices" bordered={false} bodyStyle={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <span className="stat-card-title">Warehouses / Depots</span>
                                <Title level={3} className="stat-card-value" style={{ margin: '4px 0 0 0' }}>
                                    {warehouseCount}
                                </Title>
                            </div>
                            <div className="stat-card-icon-container bg-invoices">
                                <DatabaseOutlined className="stat-card-icon color-invoices" />
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Search Controls */}
            <Card className="dashboard-card" style={{ marginBottom: 24 }} bodyStyle={{ padding: '16px 24px' }}>
                <Row gutter={16} align="middle">
                    <Col xs={24}>
                        <Input
                            placeholder="Search by shop name, manager, location, or description..."
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            value={search}
                            onChange={handleSearchChange}
                            allowClear
                            size="large"
                            style={{ borderRadius: '8px' }}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Shops Table */}
            <Card className="dashboard-card" bordered={false} bodyStyle={{ padding: '24px 0' }}>
                <Table
                    columns={columns}
                    dataSource={Array.isArray(shops) ? shops : []}
                    rowKey="id"
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    style={{ padding: '0 24px' }}
                />
            </Card>

            {/* Add/Edit Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: '700' }}>
                        <ShopOutlined style={{ color: '#1890ff' }} />
                        <span>{editingShop ? 'Edit Outlet Details' : 'Add New Outlet'}</span>
                    </div>
                }
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                okText={editingShop ? 'Update Outlet' : 'Create Outlet'}
                cancelText="Cancel"
                destroyOnClose
                bodyStyle={{ padding: '24px' }}
                style={{ borderRadius: '12px', overflow: 'hidden' }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleOk}
                >
                    <Form.Item
                        name="type"
                        label={<Text strong>Outlet Type</Text>}
                        rules={[{ required: true, message: 'Please select type' }]}
                    >
                        <Select placeholder="Select outlet type" disabled={isUpdate} suffixIcon={<DatabaseOutlined />}>
                            <Option value="shop">Retail Shop</Option>
                            <Option value="warehouse">Warehouse Depot</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label={<Text strong>Outlet Name</Text>}
                        rules={[{ required: true, message: 'Please input the outlet name!' }]}
                    >
                        <Input placeholder="e.g. Kariakoo Branch" />
                    </Form.Item>

                    <Form.Item
                        name="owner_name"
                        label={<Text strong>Manager / Owner Name</Text>}
                        rules={[{ required: true, message: 'Please input owner name!' }]}
                    >
                        <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="e.g. John Doe" />
                    </Form.Item>

                    <Form.Item
                        name="location"
                        label={<Text strong>Physical Location</Text>}
                        rules={[{ required: true, message: 'Please input location!' }]}
                    >
                        <Input prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />} placeholder="e.g. Dar es Salaam, Tanzania" />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label={<Text strong>Phone Number</Text>}
                        rules={[
                            { required: true, message: 'Please input phone number!' },
                            { pattern: /^\d{10,15}$/, message: 'Please enter a valid phone number!' }
                        ]}
                    >
                        <Input prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />} placeholder="e.g. 0712345678" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={<Text strong>Email Address</Text>}
                        rules={[
                            { required: true, message: 'Please input email!' },
                            { type: 'email', message: 'Please enter a valid email!' }
                        ]}
                    >
                        <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="e.g. branch@sellsmart.com" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={<Text strong>Description / Notes</Text>}
                        rules={[{ required: true, message: 'Please input description!' }]}
                    >
                        <Input.TextArea rows={3} placeholder="Additional branch info or notes..." style={{ borderRadius: '8px' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Shops;
